import React from 'react';
import { Grid, Typography, Box, Chip, Button } from '@mui/material';
import { EventAvailable, AttachMoney, Inventory, StorefrontOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import SimpleCard from '../../../../../components/SimpleCard';
import { APP_ROUTES } from '../../../../../constants/app-routes';

export default function AboutRetailSpace({ profile, venue }) {
  const navigate = useNavigate();

  const getDateRange = () => {
    const startDate = profile?.start_date
      ? moment(profile.start_date).format('MMM DD, YYYY')
      : 'Open';
    const endDate = profile?.end_date ? moment(profile.end_date).format('MMM DD, YYYY') : 'Ongoing';
    return `${startDate} - ${endDate}`;
  };

  const handleViewVenueProfile = () => {
    if (venue?.id) {
      const venueProfileUrl = APP_ROUTES.VENUE_PUBLIC_PROFILE.replace(':venueId', venue.id);
      navigate(venueProfileUrl);
    }
  };

  const getTotalScheduleDays = () => {
    if (!profile?.schedules || profile.schedules.length === 0) return 0;
    return profile.schedules.length;
  };

  const getPriceRange = () => {
    if (!profile?.schedules || profile.schedules.length === 0) return 'N/A';

    const prices = profile.schedules.map((s) => parseFloat(s.price_per_hour));
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    if (minPrice === maxPrice) {
      return `$${minPrice}/hr`;
    }
    return `$${minPrice} - $${maxPrice}/hr`;
  };

  return (
    <SimpleCard>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold" color="#333" gutterBottom>
            About this Retail Space
          </Typography>
        </Grid>

        {/* Date Range */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <EventAvailable sx={{ fontSize: 20, color: '#EE7B57' }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Availability Period
              </Typography>
              <Typography variant="body1" fontWeight="500">
                {getDateRange()}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Price Range */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <AttachMoney sx={{ fontSize: 20, color: '#3c99ba' }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Price Range
              </Typography>
              <Typography variant="body1" fontWeight="500">
                {getPriceRange()}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Available Days */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Inventory sx={{ fontSize: 20, color: '#666' }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Available Days
              </Typography>
              <Typography variant="body1" fontWeight="500">
                {getTotalScheduleDays()} day{getTotalScheduleDays() !== 1 ? 's' : ''} per week
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Status */}
        <Grid item xs={12}>
          <Chip
            label={profile?.status === 'active' ? 'Currently Available' : profile?.status}
            size="small"
            sx={{
              bgcolor: profile?.status === 'active' ? '#3c99ba' : '#999',
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        </Grid>

        {/* Description */}
        {venue?.description && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              About the Venue
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {venue.description}
            </Typography>
          </Grid>
        )}

        {/* View Venue Profile Button */}
        {venue?.id && (
          <Grid item xs={12}>
            <Box marginTop={2} justifyContent="center">
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleViewVenueProfile}
                sx={{
                  bgcolor: '#EE7B57',
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: '#d66a47',
                  },
                  '&:disabled': {
                    bgcolor: '#CCC',
                  },
                }}>
                View Venue Profile
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </SimpleCard>
  );
}

AboutRetailSpace.propTypes = {
  profile: PropTypes.shape({
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    status: PropTypes.string,
    schedules: PropTypes.arrayOf(
      PropTypes.shape({
        price_per_hour: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
  }).isRequired,
  venue: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
  }),
};

AboutRetailSpace.defaultProps = {
  venue: {},
};
